import { useEffect, useRef, useState, type ClipboardEvent, type DragEvent, type JSX, type ReactNode } from 'react';
import { sanitizeHtml } from '@dxtech/shared';
import {
  ChevronDownRegular, ImageRegular, LinkRegular, TextBoldRegular, TextBulletListLtrRegular, TextClearFormattingRegular, TextColorRegular,
  TextFontSizeRegular, TextItalicRegular, TextNumberListLtrRegular, TextQuoteRegular, TextUnderlineRegular,
} from '@fluentui/react-icons';

const I = { fontSize: 16 } as const; // 툴바 아이콘 크기

// 경량 Rich Text 에디터 — 무의존(contentEditable + execCommand). 사내 도구용.
// execCommand 는 표준상 deprecated 이나 전 브라우저에서 기본 서식은 동작한다.

function exec(cmd: string, arg?: string): void {
  document.execCommand(cmd, false, arg);
}

const toolStyle = (bold?: boolean, active?: boolean) => ({
  minWidth: 30, height: 30, padding: '0 8px', border: `1px solid ${active ? '#93C5FD' : '#E5E7EB'}`, background: active ? '#EFF6FF' : '#fff', borderRadius: 7,
  cursor: 'pointer', fontSize: 13, fontWeight: bold ? 800 : 600, color: '#374151', fontFamily: 'inherit',
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4,
});

const ToolBtn = ({ label, title, onClick, bold, active }: { label: ReactNode; title: string; onClick: () => void; bold?: boolean; active?: boolean }): JSX.Element => (
  <button type="button" title={title} aria-label={title}
    // mousedown 기본동작 막아 편집영역 포커스/선택 유지
    onMouseDown={(e) => { e.preventDefault(); onClick(); }}
    style={toolStyle(bold, active)}>
    {label}
  </button>
);

const COLORS = [
  ['기본', '#1F2937'], ['빨강', '#DC2626'], ['주황', '#EA580C'], ['초록', '#16A34A'],
  ['파랑', '#2563EB'], ['보라', '#7C3AED'], ['회색', '#6B7280'],
] as const;
// execCommand fontSize 1~7 → styleWithCSS 로 x-small … xxx-large
const SIZES = [['작게', '2'], ['보통', '3'], ['크게', '5'], ['아주 크게', '6']] as const;

/** 붙여넣거나 끌어온 이미지를 긴 변 1200px 이하 JPEG 로 줄여 data URI 로(본문에 포함되어 저장) */
async function shrinkImage(file: File, max = 1200): Promise<string> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error('이미지를 읽을 수 없습니다'));
      i.src = url;
    });
    const k = Math.min(1, max / Math.max(img.naturalWidth, img.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.naturalWidth * k);
    canvas.height = Math.round(img.naturalHeight * k);
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#fff'; // 투명 PNG 를 JPEG 로 바꿀 때 검은 배경 방지
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.85);
  } finally {
    URL.revokeObjectURL(url);
  }
}
const imageFiles = (list?: DataTransferItemList | FileList | null): File[] =>
  Array.from<DataTransferItem | File>(list ?? []).map((x) => (x instanceof File ? x : x.kind === 'file' ? x.getAsFile() : null))
    .filter((f): f is File => !!f && f.type.startsWith('image/'));

export function RichTextEditor({ value, onChange, minHeight = 180, placeholder = '공지 내용을 입력하세요', compact = false, rich = false }: {
  value: string; onChange: (html: string) => void; minHeight?: number; placeholder?: string;
  /** 좁은 칸용 — 굵게·기울임·밑줄·목록·서식 지우기만 */ compact?: boolean;
  /** 글자 색·크기 + 이미지 붙여넣기(주간보고) */ rich?: boolean;
}): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const [menu, setMenu] = useState<'color' | 'size' | null>(null);
  const [busy, setBusy] = useState(false);

  // 최초 1회만 초기값 주입(입력 중 커서 튐 방지)
  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== value) ref.current.innerHTML = value || '';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = (): void => { if (ref.current) onChange(sanitizeHtml(ref.current.innerHTML)); };
  // 색·크기는 <font> 대신 <span style> 로 넣도록(허용목록 정제기가 span style 의 색·크기만 통과)
  const styled = (cmd: string, arg: string): void => { exec('styleWithCSS', 'true'); exec(cmd, arg); exec('styleWithCSS', 'false'); setMenu(null); emit(); };

  const insertImages = async (files: File[]): Promise<void> => {
    if (!files.length) return;
    setBusy(true);
    try {
      ref.current?.focus();
      for (const f of files) exec('insertImage', await shrinkImage(f));
      emit();
    } catch { /* 읽을 수 없는 이미지는 건너뜀 */ } finally { setBusy(false); }
  };
  const onPaste = (e: ClipboardEvent<HTMLDivElement>): void => {
    if (!rich) return;
    const files = imageFiles(e.clipboardData?.items);
    if (files.length) { e.preventDefault(); void insertImages(files); }
  };
  const onDrop = (e: DragEvent<HTMLDivElement>): void => {
    if (!rich) return;
    const files = imageFiles(e.dataTransfer?.files);
    if (files.length) { e.preventDefault(); void insertImages(files); }
  };

  const addLink = (): void => {
    const url = window.prompt('링크 URL을 입력하세요 (https://...)');
    if (url && /^https?:\/\//i.test(url)) { exec('createLink', url); emit(); }
  };

  const pop = (children: ReactNode) => (
    <div onMouseDown={(e) => e.preventDefault()}
      style={{ position: 'absolute', top: 34, left: 0, zIndex: 30, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 10, boxShadow: '0 10px 24px rgba(16,24,40,.14)', padding: 6 }}>
      {children}
    </div>
  );

  return (
    <div style={{ border: '1px solid #D1D5DB', borderRadius: 10, overflow: 'visible', background: '#fff' }}>
      <div style={{ display: 'flex', gap: compact ? 4 : 6, flexWrap: 'wrap', padding: compact ? 6 : 8, borderBottom: '1px solid #EDEFF2', background: '#F9FAFB', borderRadius: '10px 10px 0 0' }}>
        <ToolBtn label={<TextBoldRegular style={I} />} title="굵게" onClick={() => { exec('bold'); emit(); }} />
        <ToolBtn label={<TextItalicRegular style={I} />} title="기울임" onClick={() => { exec('italic'); emit(); }} />
        <ToolBtn label={<TextUnderlineRegular style={I} />} title="밑줄" onClick={() => { exec('underline'); emit(); }} />
        {rich && (
          <>
            <span style={{ position: 'relative' }}>
              <ToolBtn title="글자 색" active={menu === 'color'} onClick={() => setMenu(menu === 'color' ? null : 'color')}
                label={<TextColorRegular style={I} />} />
              {menu === 'color' && pop(
                <div style={{ display: 'flex', gap: 4 }}>
                  {COLORS.map(([name, c]) => (
                    <button key={c} type="button" title={name} aria-label={`글자 색 ${name}`} onMouseDown={(e) => { e.preventDefault(); styled('foreColor', c); }}
                      style={{ width: 22, height: 22, borderRadius: 6, border: '1px solid #E5E7EB', background: c, cursor: 'pointer' }} />
                  ))}
                </div>,
              )}
            </span>
            <span style={{ position: 'relative' }}>
              <ToolBtn title="글자 크기" active={menu === 'size'} onClick={() => setMenu(menu === 'size' ? null : 'size')} label={<><TextFontSizeRegular style={I} /><ChevronDownRegular style={{ fontSize: 10 }} /></>} />
              {menu === 'size' && pop(
                <div style={{ display: 'flex', flexDirection: 'column', minWidth: 96 }}>
                  {SIZES.map(([name, v]) => (
                    <button key={v} type="button" onMouseDown={(e) => { e.preventDefault(); styled('fontSize', v); }}
                      style={{ border: 'none', background: 'transparent', textAlign: 'left', padding: '5px 8px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', fontSize: { '2': 12, '3': 13.5, '5': 17, '6': 21 }[v] }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#F3F4F6'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>{name}</button>
                  ))}
                </div>,
              )}
            </span>
          </>
        )}
        <span style={{ width: 1, background: '#E5E7EB', margin: '2px 2px' }} />
        <ToolBtn label={<TextBulletListLtrRegular style={I} />} title="글머리 목록" onClick={() => { exec('insertUnorderedList'); emit(); }} />
        <ToolBtn label={<TextNumberListLtrRegular style={I} />} title="번호 목록" onClick={() => { exec('insertOrderedList'); emit(); }} />
        {!compact && <ToolBtn label={<TextQuoteRegular style={I} />} title="인용" onClick={() => { exec('formatBlock', 'blockquote'); emit(); }} />}
        {rich && (
          <>
            <ToolBtn label={<ImageRegular style={I} />} title="이미지 넣기 (복사한 이미지는 Ctrl+V 로 붙여넣기)" onClick={() => fileRef.current?.click()} />
            <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={(e) => { void insertImages(imageFiles(e.target.files)); e.target.value = ''; }} />
          </>
        )}
        <span style={{ width: 1, background: '#E5E7EB', margin: '2px 2px' }} />
        {!compact && <ToolBtn label={<LinkRegular style={I} />} title="링크" onClick={addLink} />}
        <ToolBtn label={<TextClearFormattingRegular style={I} />} title="서식 지우기" onClick={() => { exec('removeFormat'); emit(); }} />
        {busy && <span style={{ fontSize: 11.5, color: '#2563EB', alignSelf: 'center' }}>이미지 넣는 중…</span>}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onPaste={onPaste}
        onDrop={onDrop}
        onFocus={() => setMenu(null)}
        data-placeholder={placeholder}
        className="dx-rte"
        style={{ minHeight, padding: compact ? '10px 12px' : '12px 14px', fontSize: compact ? 13 : 14, lineHeight: 1.7, color: '#1F2937', outline: 'none' }}
      />
    </div>
  );
}

/** 정제된 공지 본문 렌더 뷰어 */
export function RichTextView({ html }: { html: string }): JSX.Element {
  return <div className="dx-rte-view" style={{ fontSize: 14, lineHeight: 1.75, color: '#1F2937', wordBreak: 'break-word' }}
    dangerouslySetInnerHTML={{ __html: sanitizeHtml(html) }} />;
}
